import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, Clock, Sparkles, MessageSquare, Lock } from 'lucide-react';
import { MentorFoundingMemberBadge } from './MentorFoundingMemberBadge';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import type { MentorProfile } from '@/types/mentorship';

interface MentorCardProps {
  mentor: MentorProfile;
  matchingSkills?: string[];
  matchScore?: number;
  onRequestMentorship: (mentorId: string, skillFocus: string, message: string) => Promise<boolean>;
  hasExistingConnection?: boolean;
  isPro?: boolean;
}

export function MentorCard({ mentor, matchingSkills = [], matchScore, onRequestMentorship, hasExistingConnection, isPro = true }: MentorCardProps) {
  const [open, setOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const handleRequestClick = () => {
    if (!isPro) {
      setUpgradeModalOpen(true);
      return;
    }
    setOpen(true);
  };

  const getInitials = () => {
    if (mentor.first_name && mentor.last_name) {
      return `${mentor.first_name[0]}${mentor.last_name[0]}`.toUpperCase();
    }
    if (mentor.display_name) {
      return mentor.display_name.slice(0, 2).toUpperCase();
    }
    return 'M';
  };

  const getName = () => {
    if (mentor.first_name && mentor.last_name) {
      return `${mentor.first_name} ${mentor.last_name}`;
    }
    return mentor.display_name || 'Mentor';
  };

  const handleSubmit = async () => {
    if (!selectedSkill) return;
    
    setSending(true);
    const success = await onRequestMentorship(mentor.user_id, selectedSkill, message);
    setSending(false);
    
    if (success) {
      setOpen(false);
      setSelectedSkill('');
      setMessage('');
    }
  };

  return (
    <Card className="card-elevated hover:border-primary/30 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-4">
          <Avatar className="w-14 h-14">
            <AvatarImage src={mentor.avatar_url || undefined} alt={getName()} />
            <AvatarFallback className="bg-primary/10 text-primary text-lg">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg truncate">{getName()}</CardTitle>
              <MentorFoundingMemberBadge isFoundingMember={mentor.founding_member} />
              {matchScore !== undefined && matchScore > 0 && (
                <Badge variant="default" className="shrink-0 bg-primary/20 text-primary border-primary/30">
                  {matchScore}% match
                </Badge>
              )}
            </div>
            <CardDescription className="flex items-center gap-2 mt-1">
              {mentor.job_role && (
                <span className="truncate">{mentor.job_role}</span>
              )}
              {mentor.years_experience && (
                <Badge variant="outline" className="shrink-0">
                  {mentor.years_experience}+ yrs
                </Badge>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {mentor.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">{mentor.bio}</p>
        )}

        {/* Show AI matching skills if available */}
        {matchingSkills.length > 0 && matchScore !== undefined && (
          <div className="space-y-2 p-2 bg-primary/5 rounded-md border border-primary/10">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Sparkles className="w-4 h-4" />
              <span>AI Skill Matches:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {matchingSkills.slice(0, 4).map((skill, i) => (
                <Badge 
                  key={i} 
                  variant="secondary"
                  className="bg-primary/20 text-primary border-primary/30 text-xs"
                >
                  {skill}
                </Badge>
              ))}
              {matchingSkills.length > 4 && (
                <Badge variant="outline" className="text-xs">+{matchingSkills.length - 4}</Badge>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2 min-h-[4.5rem]">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>Can teach:</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {mentor.skills_can_teach.slice(0, 3).map((skill) => (
              <Badge
                key={skill}
                variant="secondary"
              >
                {skill}
              </Badge>
            ))}
            {mentor.skills_can_teach.length > 3 && (
              <Badge variant="outline">+{mentor.skills_can_teach.length - 3}</Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border/50">
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span>Up to {mentor.max_mentees} mentees</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="capitalize">{mentor.preferred_meeting_frequency}</span>
          </div>
        </div>

        {/* Request/Connect Button */}
        {!isPro ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  className="w-full" 
                  variant="secondary"
                  onClick={handleRequestClick}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Message (Pro)
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Upgrade to Pro to connect with mentors</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button 
                className="w-full" 
                disabled={hasExistingConnection}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {hasExistingConnection ? 'Request Pending' : 'Request Mentorship'}
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Mentorship from {getName()}</DialogTitle>
              <DialogDescription>
                Select a skill you'd like to learn and introduce yourself.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Skill to learn</Label>
                <Select value={selectedSkill} onValueChange={setSelectedSkill}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a skill" />
                  </SelectTrigger>
                  <SelectContent>
                    {mentor.skills_can_teach.map((skill) => (
                      <SelectItem key={skill} value={skill}>
                        {skill}
                        {matchingSkills.includes(skill) && ' ✨'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Introduction message (optional)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell them a bit about yourself and why you want to learn this skill..."
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!selectedSkill || sending}>
                {sending ? 'Sending...' : 'Send Request'}
              </Button>
            </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <UpgradeModal
          isOpen={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          trigger="mentor_message"
        />
      </CardContent>
    </Card>
  );
}
